/**
 * CheckpointComparisonModal
 * Full-screen modal for comparing and selecting checkpoints with larger preview images.
 * Provides a better UX for choosing the best checkpoint after training completes.
 */

import { useState } from "react";
import { Check, Loader2, Expand, Sparkles, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { CheckpointSampleThumbnail } from "@/components/loras/CheckpointSampleThumbnail";
import { cn } from "@/lib/utils";
import type { Checkpoint } from "@/types/loras";

const USABLE_CHECKPOINT: ReadonlySet<string> = new Set([
  "available",
  "evaluating",
  "evaluated",
  "ready",
]);

interface CheckpointComparisonModalProps {
  checkpoints: Checkpoint[];
  defaultCheckpointId?: string | null;
  onSelect: (checkpointId: string) => void;
  settingDefault?: boolean;
  /** Trigger button element - if not provided, renders a default button */
  trigger?: React.ReactNode;
}

export function CheckpointComparisonModal({
  checkpoints,
  defaultCheckpointId,
  onSelect,
  settingDefault,
  trigger,
}: CheckpointComparisonModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(
    defaultCheckpointId ?? null,
  );

  if (checkpoints.length === 0) return null;

  // Sort by step number descending (highest first)
  const sortedCheckpoints = [...checkpoints].sort(
    (a, b) => b.stepNumber - a.stepNumber,
  );

  const handleSelect = (checkpointId: string) => {
    setSelectedId(checkpointId);
  };

  const handleConfirm = () => {
    if (selectedId) {
      onSelect(selectedId);
      setIsOpen(false);
    }
  };

  const defaultTrigger = (
    <Button variant="outline" size="sm" className="gap-2">
      <Expand className="w-4 h-4" />
      Compare Checkpoints
    </Button>
  );

  return (
    <>
      <div onClick={() => setIsOpen(true)}>{trigger ?? defaultTrigger}</div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-primary" />
              Compare Training Checkpoints
            </DialogTitle>
            <DialogDescription>
              Select the checkpoint that best represents your LoRA. Higher step
              numbers generally produce better results, but earlier checkpoints
              may avoid overfitting.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {sortedCheckpoints.map((checkpoint) => {
                const isSelected = selectedId === checkpoint.id;
                const isCurrent = defaultCheckpointId === checkpoint.id;
                const usable = USABLE_CHECKPOINT.has(checkpoint.statusSlug);
                const hasSamples =
                  checkpoint.sampleInferenceAssetIds &&
                  checkpoint.sampleInferenceAssetIds.length > 0;

                return (
                  <button
                    key={checkpoint.id}
                    type="button"
                    disabled={!usable || settingDefault}
                    onClick={() => usable && handleSelect(checkpoint.id)}
                    className={cn(
                      "relative rounded-xl border-2 p-3 text-left transition-all",
                      isSelected
                        ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                        : "border-white/10 hover:border-white/30 hover:bg-white/5",
                      !usable && "opacity-50 cursor-not-allowed",
                    )}
                  >
                    {/* Step badge */}
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-foreground">
                        Step {checkpoint.stepNumber}
                      </span>
                      <div className="flex items-center gap-2">
                        {isCurrent && (
                          <Badge
                            variant="outline"
                            className="text-[10px] border-primary/50 text-primary"
                          >
                            Current
                          </Badge>
                        )}
                        {isSelected && (
                          <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                            <Check className="w-3 h-3 text-primary-foreground" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Sample images grid */}
                    {hasSamples ? (
                      <div className="grid grid-cols-2 gap-1.5">
                        {checkpoint
                          .sampleInferenceAssetIds!.slice(0, 4)
                          .map((assetId, idx) => (
                            <div
                              key={assetId}
                              className="aspect-square rounded-lg overflow-hidden bg-muted"
                            >
                              <CheckpointSampleThumbnail
                                assetId={assetId}
                                alt={`Step ${checkpoint.stepNumber} sample ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="aspect-video rounded-lg bg-muted flex items-center justify-center">
                        <span className="text-xs text-muted-foreground">
                          No preview available
                        </span>
                      </div>
                    )}

                    {/* Status indicator */}
                    <div className="mt-2 text-[11px] text-muted-foreground">
                      Status: {checkpoint.statusSlug}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-t bg-muted/30">
            <div className="text-sm text-muted-foreground">
              {sortedCheckpoints.length} checkpoint
              {sortedCheckpoints.length !== 1 ? "s" : ""} available
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={!selectedId || settingDefault}
              >
                {settingDefault ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Use This Checkpoint
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
